import { useLazyQuery, useMutation } from "@apollo/client";
import { Avatar, Button, Collapse, Dropdown, message, Popover, Skeleton, Tag, Tooltip } from "antd";
import {
  LuBriefcaseBusiness,
  LuCalendarDays,
  LuClipboardList,
  LuEllipsisVertical,
  LuPhone,
  LuSquarePen,
} from "react-icons/lu";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useDispatch, useSelector } from "react-redux";
import { OPT_OUT_LEAD, UPDATE_LEAD } from "../../../graphql/mutation";
import { GET_LEAD_OPPORTUNITIES_BY_IDS } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import DownIcon from "../../../icons/DownIcon";
import AddLeadIcon from "../../../icons/js/LeadProfileIcons/AddLeadIcon";
import { EditPencilIcon } from "../../../icons/js/PencilIcon";
import PlusOutLinedJSX from "../../../icons/js/PlusOutLinedJSX";
import { AccessPermissionAction } from "../../../library/constants";
import { checkAccessPermission, closeDialog, openDialog } from "../../../library/helpers";
import { setSelectedLeadRedux, mergeLeadDetail } from "../../../redux/slices/leadSlice";
import { setLoaderStatus } from "../../../redux/slices/loaderSlice";
import { setLocationIdSlice } from "../../../redux/slices/locationSlice";
import { convertToInitials } from "../../layoutComponent/layoutHelpers";
import CustomButton from "../../commonComponents/button";
import { makeVoiceCall } from "../../../utils";
import AppointmentList from "../appointmentList";
import { AboutThisLead } from "./AboutThisLead";
import { LeadNotes } from "./LeadNotes";
import { NameForm } from "./NameForm";
import { NotesDrawer } from "./NotesDrawer";
import { TaskDrawer } from "./TaskDrawer";
import WishlistDrawer from "./WishlistDrawer";
// import ChatDrawer from "./ChatDrawer";
import CampignListView from "./leadCampaigns/campignListView";
import LeadConsent from "./leadConsent";
import LeadEditDrawer from "./leadEditDrawer";
import { LeadTaskList } from "./leadTask";
import ShiftLeadDrawer from "./ShiftLeadDrawer";
import ConsentAuditLogDrawer from "./auditLog/ConsentAuditLogDrawer";
import { OpportunityDrawer } from "./opporotunityLayer/opportunityDrawer";
import OpportunityLayer from "./opporotunityLayer/opportunityLayer";
import OppTypeRootTabs from "./OppTypeRootTabs";
import { getRootOppTypeId, oppMatchesTab } from "../../../library/oppTypeScoping";
import { ALL_OPPORTUNITY_TYPES_ID } from "../../../helpers/opportunityHelper";

// Shared sizing for the lead action row (Opportunity / Task / Note /
// Appointment / Call) so the lucide glyphs render as one set. No `color` prop —
// lucide defaults to `currentColor`, so the tint comes from `.icon-action-color`
// and `group-hover:text-inherit` hands it back on hover.
const ACTION_ICON_PROPS = {
  size: 18,
  strokeWidth: 1.75,
  className: "icon-action-color group-hover:text-inherit",
};

const LeadProfile = props => {
  const {
    socket,
    handleAddAppointment,
    setConnection,
    handleAddPhoneClick,
    setActiveOpp,
    refetchAppointment,
    handleSendConsentClick,
    handleActiveToggle,
    user,
    updateStatusLoading = false,
    setFilters,
    setRefetchAppointment,
    refetchLeads,
    isAppointmentChangeFromLead,
    disableConcentClick,
    consentLoader,
    statusList,
    crmIntegration,
    crmIntegrationData,
    handleLeadDetailProfile,
    setIsClickonPotentialDuplicate,
    getLeadDetail,
    eventId,
    spinner,
    refetchLeadDetails = () => {},
    setLeadList,
    leadList,
    getMessages = () => {},
    activeOpportunity,
    showAdd = true,
    languages,
    refreshLeadListData,
    // Opp-type-scoped tab — root opp_type id of the active Sales /
    // Service / Parts tab. Threaded down to all section components so
    // their queries refetch on tab switch. ``null`` ⇒ no tab filter.
    selectedOppTypeId,
    setSelectedOppTypeId,
    // Per-root unread counter — incoming socket messages whose
    // root_opp_type_id doesn't match the active tab. ``OppTypeRootTabs``
    // renders a red dot on tabs with count > 0.
    unreadByRoot = {},
  } = props;
  const dispatch = useDispatch();
  const nameEditFormRef = useRef();

  const [wishlistDrawerVisible, setWishlistDrawerVisible] = useState(false);
  const [shiftLeadDrawerVisible, setShiftLeadDrawerVisible] = useState(false);
  const [auditLogDrawerVisible, setAuditLogDrawerVisible] = useState(false);

  const [lang] = useLang();
  const [showNameEdit, setshowNameEdit] = useState(false);
  const [liveCampaign, setliveCampaign] = useState(false);
  const [opportunityDrawer, setopportunityDrawer] = useState({
    open: false,
    item: {},
  });
  const [leadFormDrawer, setleadFormDrawer] = useState({
    open: false,
    item: {},
  });
  // const [chatDrawerData, setChatDrawerData] = useState({
  //   open: false,
  //   selectedLead: null,
  // });
  const { selectedLead, messageOpportunity } = useSelector(state => state.lead);
  const isAllOpportunitySelected = messageOpportunity === ALL_OPPORTUNITY_TYPES_ID;
  // Section counts shown in the accordion header (e.g. "Notes (3)").
  //
  // Source: ``leadNew`` now returns ``notesCountByOppType`` /
  // ``tasksCountByOppType`` / ``appointmentsCountByOppType`` as
  // ``[{rootOppTypeId, count}]`` arrays grouped by ROOT opp_type.
  // ``rootOppTypeId = null`` means "opp-less" (notes/tasks not tied
  // to a specific opportunity).
  //
  // Display rule per product:
  //   count = opp-less.count + active-tab.count
  // e.g. lead has 2 general notes + 1 Sales note + 3 Service notes:
  //   Sales tab  → 2 + 1 = 3
  //   Service tab → 2 + 3 = 5
  // When no tab is selected (legacy/unscoped lead), show the sum of
  // every bucket so the header still reflects reality.
  //
  // Counts come back with ``leadNew``, so the accordion header shows
  // the right number BEFORE the panel ever expands. Previously the
  // counts came from Redux state populated by ``getLeadNotes`` /
  // ``getLeadTasks``, which only fired on expand → showed 0.
  const sumCountsByOppType = (rows, activeTabId) => {
    if (!Array.isArray(rows)) return 0;
    if (!activeTabId) {
      return rows.reduce((sum, r) => sum + (r?.count || 0), 0);
    }
    let total = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (r?.rootOppTypeId == null || Number(r?.rootOppTypeId) === Number(activeTabId)) {
        total += r?.count || 0;
      }
    }
    return total;
  };
  // Local optimistic deltas — added on top of the BE-derived counts so
  // the accordion header reflects newly-added records BEFORE a fresh
  // ``leadNew`` lands. Resets whenever the BE count array reference
  // changes (the next refetch arrives and absorbs the delta).
  //
  // Wiring: notes and tasks each maintain a ``notes[]`` / ``tasks[]``
  // in their Redux slice; ADD reducers unshift/push the new record. We
  // watch the array length, look at the newly-added row to resolve its
  // root opp_type via ``selectedLead.leadOpportunities``, and bump the
  // matching bucket in a ``{ rootId|'none': delta }`` map.
  //
  // Why per-root (not flat): a +1 must only show on the tab that the
  // record belongs to. The previous flat-delta version made the +1
  // bleed into every tab until a lead-switch refetched. Opp-less rows
  // (no leadOpportunity) go into the ``'none'`` bucket — those should
  // surface on every tab (matching what the BE refetch will return).
  //
  // Appointments don't have an ADD-with-payload Redux action yet —
  // ``refetchAppointment`` already triggers a leadNew refetch, so
  // they're left as a no-op for now.
  const reduxNotes = useSelector(state => state?.notes?.notes);
  const reduxTasks = useSelector(state => state?.tasks?.tasks);
  // Snapshot of the array length at the last reset (when fresh BE
  // counts landed). Anything beyond this is an optimistic ADD.
  const [notesBaselineLen, setNotesBaselineLen] = useState((reduxNotes || []).length);
  const [tasksBaselineLen, setTasksBaselineLen] = useState((reduxTasks || []).length);
  const [notesAddDeltaByRoot, setNotesAddDeltaByRoot] = useState({});
  const [tasksAddDeltaByRoot, setTasksAddDeltaByRoot] = useState({});

  // Reset deltas + baseline length whenever a fresh ``leadNew`` lands
  // OR the user switches leads. The BE counts now include any record
  // we previously bumped optimistically, so the local delta must be
  // cleared to avoid double-counting. We also re-anchor the baseline
  // to the CURRENT Redux array length so the next ADD walk treats
  // anything beyond it as a real new record.
  useEffect(() => {
    setNotesAddDeltaByRoot({});
    setNotesBaselineLen((reduxNotes || []).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.id, selectedLead?.notesCountByOppType]);
  useEffect(() => {
    setTasksAddDeltaByRoot({});
    setTasksBaselineLen((reduxTasks || []).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.id, selectedLead?.tasksCountByOppType]);

  // Resolve the root opp_type id of a record by looking its
  // ``leadOpportunityId`` (or the short ``leadOpportunity.id``) up
  // against the lead's full opp list (which carries the opp_type chain).
  // Returns ``'none'`` for opp-less records so they bucket correctly.
  const resolveRootBucket = record => {
    const leadOppId = record?.leadOpportunityId || record?.leadOpportunity?.id;
    if (!leadOppId) return "none";
    const opp = (selectedLead?.leadOpportunities || []).find(
      o => Number(o?.id) === Number(leadOppId)
    );
    const rootId = getRootOppTypeId(opp?.opportunityType);
    return rootId ? Number(rootId) : "none";
  };

  // Walk past-baseline notes/tasks and capture them into the per-root
  // delta map. Idempotent: only counts items beyond the current
  // ``baselineLen + sum(delta)`` so re-renders don't double-bump.
  useEffect(() => {
    const all = reduxNotes || [];
    const recordedSoFar = Object.values(notesAddDeltaByRoot).reduce((s, v) => s + v, 0);
    const expectedConsumed = notesBaselineLen + recordedSoFar;
    if (all.length <= expectedConsumed) return;
    // notes are unshifted on ADD, so the NEWEST items live at index 0.
    // The "unconsumed" ones are all[0 .. (all.length - expectedConsumed - 1)].
    const newOnes = all.slice(0, all.length - expectedConsumed);
    setNotesAddDeltaByRoot(prev => {
      const next = { ...prev };
      newOnes.forEach(rec => {
        const bucket = resolveRootBucket(rec);
        next[bucket] = (next[bucket] || 0) + 1;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxNotes, notesBaselineLen]);

  useEffect(() => {
    const all = reduxTasks || [];
    const recordedSoFar = Object.values(tasksAddDeltaByRoot).reduce((s, v) => s + v, 0);
    const expectedConsumed = tasksBaselineLen + recordedSoFar;
    if (all.length <= expectedConsumed) return;
    // tasks are pushed on ADD, so the NEWEST items live at the END.
    const newOnes = all.slice(expectedConsumed);
    setTasksAddDeltaByRoot(prev => {
      const next = { ...prev };
      newOnes.forEach(rec => {
        const bucket = resolveRootBucket(rec);
        next[bucket] = (next[bucket] || 0) + 1;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxTasks, tasksBaselineLen]);

  // Pick the right slice of a per-root delta map for the active tab.
  // Mirrors ``sumCountsByOppType`` — opp-less ('none') items show on
  // every tab; root-tagged items show only on their root.
  const deltaForTab = (mapByRoot, activeTabId) => {
    if (!mapByRoot) return 0;
    if (!activeTabId) {
      return Object.values(mapByRoot).reduce((s, v) => s + v, 0);
    }
    const tabSlice = mapByRoot[Number(activeTabId)] || 0;
    const oppLess = mapByRoot.none || 0;
    return tabSlice + oppLess;
  };

  const notesCount = useMemo(
    () =>
      sumCountsByOppType(selectedLead?.notesCountByOppType, selectedOppTypeId) +
      deltaForTab(notesAddDeltaByRoot, selectedOppTypeId),
    [selectedLead?.notesCountByOppType, selectedOppTypeId, notesAddDeltaByRoot]
  );
  const tasksCount = useMemo(
    () =>
      sumCountsByOppType(selectedLead?.tasksCountByOppType, selectedOppTypeId) +
      deltaForTab(tasksAddDeltaByRoot, selectedOppTypeId),
    [selectedLead?.tasksCountByOppType, selectedOppTypeId, tasksAddDeltaByRoot]
  );
  const appointmentsCount = useMemo(
    () => sumCountsByOppType(selectedLead?.appointmentsCountByOppType, selectedOppTypeId),
    [selectedLead?.appointmentsCountByOppType, selectedOppTypeId]
  );
  const opportunitiesCount = useMemo(() => {
    const opps = selectedLead?.leadOpportunities || [];
    if (!selectedOppTypeId) return opps.length;
    return opps.filter(opp => oppMatchesTab(opp, selectedOppTypeId)).length;
  }, [selectedLead?.leadOpportunities, selectedOppTypeId]);
  const canEdit = checkAccessPermission(AccessPermissionAction.LEAD_ADD_EDIT);
  const canViewAppointment = checkAccessPermission(AccessPermissionAction.APPOINTMENT_VIEW);
  const canChangeLocation = checkAccessPermission(
    AccessPermissionAction.LEAD_MANAGEMENT_SEND_TO_STORE
  );
  const setSelectedLead = lead => {
    // The profile panel (phone/email pencil drawers, primary toggles, notes,
    // preferred language, etc.) only edits lead fields — never the conversation.
    // For a same-lead update, merge through the reducer so ``messages`` are
    // preserved (setSelectedLeadRedux wipes them, and a stale/partial lead here
    // blanked the open thread). A clear ({}) or an actual lead switch falls
    // through to the full set.
    const sameLead = lead?.id && selectedLead?.id && +lead.id === +selectedLead.id;
    if (sameLead) {
      dispatch(mergeLeadDetail(lead));
    } else {
      dispatch(setSelectedLeadRedux(lead));
    }
  };

  const [getLeadOpportunities] = useLazyQuery(GET_LEAD_OPPORTUNITIES_BY_IDS);

  const [leadNotesData, setleadNotesData] = useState({
    open: false,
    leadNote: null,
    selectedLead: null,
    mode: "",
  });

  const [optOutLead, { loading: optedOutLoader }] = useMutation(OPT_OUT_LEAD, {
    // Without an onError the mutate promise REJECTS on any network/GraphQL
    // failure, which used to leave handleOptedOut's global loader raised and
    // the confirm dialog masking the whole screen (EAI-1820). Handle the error
    // here so the promise resolves and the caller's teardown always runs.
    onError: err => {
      message.error(err?.message || lang.somethingWentWrong);
    },
    onCompleted: res => {
      if (res?.optOutLead?.statusCode === 200) {
        message.success(res?.optOutLead?.message);
        getLeadDetail({ variables: { leadId: +selectedLead?.id } });
        // The consent change is recorded as a CONSENT_UPDATE message, so the
        // thread has to reload for it to appear. See LeadConsent for the other
        // consent controls that do the same.
        getMessages();
      } else {
        message.error(res?.optOutLead?.message || lang.somethingWentWrong);
      }
    },
  });
  const [updateLead, { loading: updateLeadLoader }] = useMutation(UPDATE_LEAD);
  const handleOptedOut = async () => {
    dispatch(setLoaderStatus({ open: true }));
    try {
      await optOutLead({ variables: { leadId: +selectedLead?.id } });
    } catch (err) {
      // onError above already reports network/GraphQL failures; this catches a
      // throw from inside onCompleted itself (getLeadDetail / getMessages).
      console.error("optOutLead failed:", err);
    } finally {
      // `loader.open` is a GLOBAL flag with no timeout — leaving it true wedges
      // every confirm dialog for the rest of the session, and inside the CRM
      // iframe the only way back is logging out. Always release it.
      dispatch(setLoaderStatus({ open: false }));
      closeDialog("confirmDialog");
    }
  };

  const handleLeadClick = (leadId, locationId) => {
    if (selectedLead?.id === leadId) {
      return;
    }
    if (+sessionStorage.getItem("currentLocationId") !== +locationId) {
      localStorage.setItem("changedLocationLeadId", leadId);
      sessionStorage.setItem("currentLocationId", locationId);
      setSelectedLead({});
      dispatch(setLocationIdSlice({ locationId: locationId }));
      setIsClickonPotentialDuplicate(true);
      refetchLeads();
      return;
    }

    handleLeadDetailProfile(leadId);
  };

  const handlePotentialLeadPopup = () => {
    openDialog("potentialDuplicateLeads", {
      dialogHeader: lang.potentialDuplicateLeads,
      setModalVisible: handlePotentialLeadPopup,
      isFromActivity: true,
      leadId: selectedLead?.id,
      selectedLeadPotentialIds: selectedLead?.phoneNumbers[0]?.potentialLeads,
      handleLeadClick: handleLeadClick,
      refetchLeads: refetchLeads,
      crmIntegrationData: crmIntegrationData,
      isFromLeadCenter: false,
      crmIntegrationType: crmIntegration,
    });
  };

  const handleSocketMessage = event => {
    const data = JSON.parse(event.data);
    if (data.type === "webchat_disconnect") {
      setConnection(data.payload);
    }
  };

  useEffect(() => {
    if (socket) {
      // Add event listener to the socket.
      socket.addEventListener("message", handleSocketMessage);

      // Clean up function to remove the event listener.
      // This prevents multiple event listeners from being added every time the component re-renders.
      return () => {
        socket.removeEventListener("message", handleSocketMessage);
      };
    }
  }, []);

  const canAddEditOpportunity = checkAccessPermission(
    AccessPermissionAction.LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT
  );

  // socket &&
  //   socket.on("webchat_disconnect", arg => {
  //     setConnection(arg);
  //   });
  const [leadTaskData, setleadTaskData] = useState({
    open: false,
    leadTask: null,
    selectedLead: null,
    mode: "",
  });
  const profileEmail =
    selectedLead &&
    selectedLead.emails &&
    selectedLead.emails.length > 0 &&
    selectedLead.emails.find(item => item?.isPrimary)?.email;

  const canAddTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);
  const canAudioCall = checkAccessPermission(AccessPermissionAction.CALL_MANAGEMENT_AUDIO);

  const handleVoiceCall = async () => {
    if (!selectedLead?.id || !user?.id) {
      message.error("Missing lead or user information");
      return;
    }

    const currentLocationId = sessionStorage.getItem("currentLocationId");
    const locationId = currentLocationId?.includes(",")
      ? currentLocationId.split(",")[0]
      : currentLocationId;

    if (!locationId) {
      message.error("Missing location information");
      return;
    }

    await makeVoiceCall({
      location_id: locationId,
      user_id: user.id.toString(),
      lead_id: selectedLead.id.toString(),
    });
  };

  // TODO: Define lead action buttons for profile (Opportunities, Task, Note, Appointment, Call, Chat)
  const leadActions = [
    {
      label: "Opportu..",
      // Labels are clipped to fit the 5-column grid; the tooltip carries the
      // full word.
      tooltip: "Opportunity",
      // Same glyph the opportunity chip uses for the Sales root
      // (oppTypeRootConfig), so this button matches what it opens.
      icon: <LuBriefcaseBusiness {...ACTION_ICON_PROPS} />,
      onClick: () => setopportunityDrawer({ open: true, item: selectedLead }),
    },
    {
      label: "Task",
      icon: <LuClipboardList {...ACTION_ICON_PROPS} />,
      onClick: () =>
        canAddTask
          ? setleadTaskData({ open: true, mode: "ADD", selectedLead })
          : message.info(lang.noTaskPermission),
    },
    {
      label: "Note",
      icon: <LuSquarePen {...ACTION_ICON_PROPS} />,
      onClick: () =>
        setleadNotesData({
          ...leadNotesData,
          open: true,
          selectedLead,
          mode: "ADD",
        }),
    },
    {
      label: "Appoint...",
      tooltip: "Appointment",
      icon: <LuCalendarDays {...ACTION_ICON_PROPS} />,
      onClick: () => handleAddAppointment(),
    },
    {
      label: "Call",
      icon: <LuPhone {...ACTION_ICON_PROPS} />,
      onClick: () =>
        canAudioCall
          ? handleVoiceCall()
          : message.warning("You don't have permission to make audio calls"),
    },
    // TODO - Hide chat option for now
    // {
    //   label: "Chat",
    //   icon: <ChatIcon />,
    //   onClick: () => setChatDrawerData({ open: true, selectedLead }),
    // },
  ];
  const phoneArr = ["Mobile", "Work", crmIntegration === "DS" ? "Other" : "Home"];

  const profileSections = [
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          About this lead{" "}
          <Button
            size="small"
            disabled={!selectedLead?.id}
            onClick={e => {
              e.stopPropagation();
              setleadFormDrawer({
                open: true,
                lead: selectedLead,
                mode: "EDIT",
                showOpportunityForm: false,
                header: (
                  <div className="flex flex-col">
                    <span className="text-lg font-[600]">Modify Lead Information</span>
                  </div>
                ),
                setSelectedLead,
                setLeadList,
                refetch: () => {},
                refetchLead: () => {},
                getLeads: () => {},
                refetchLeadOpportunities: () => {},
              });
            }}
          >
            Edit
          </Button>
        </span>
      ),
      content: (
        <AboutThisLead
          leadList={leadList}
          getMessages={getMessages}
          setSelectedLead={setSelectedLead}
          setLeadList={setLeadList}
          languages={languages}
        />
      ),
    },
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          Consents{" "}
          <Button
            type="text"
            className="text-brand-500 gap-1 flex-row-centered"
            size="small"
            disabled={!selectedLead?.id}
            onClick={e => {
              // Must run first — without it the click also toggles the panel.
              e.stopPropagation();
              setAuditLogDrawerVisible(true);
            }}
          >
            {lang.auditLog}
          </Button>
        </span>
      ),
      content: (
        <LeadConsent
          consentLoader={consentLoader}
          refetchMessages={getMessages}
          selectedLead={selectedLead}
          disableConcentClick={disableConcentClick}
          handleSendConsentClick={handleSendConsentClick}
          user={user}
          optedOutLoader={optedOutLoader}
          handleOptedOut={handleOptedOut}
          updateStatusLoading={updateStatusLoading}
          handleActiveToggle={handleActiveToggle}
        />
      ),
    },
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          Notes ({notesCount}){" "}
          <Button
            type="text"
            className="text-brand-500 gap-1 flex-row-centered"
            size="small"
            onClick={e => {
              e.stopPropagation();
              setleadNotesData({
                ...leadNotesData,
                open: true,
                selectedLead,
                mode: "ADD",
              });
            }}
          >
            <PlusOutLinedJSX />
            Add
          </Button>
        </span>
      ),
      content: (
        <LeadNotes
          setSelectedLead={setSelectedLead}
          leadNotesData={leadNotesData}
          setleadNotesData={setleadNotesData}
          selectedOppTypeId={selectedOppTypeId}
        />
      ),
    },
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          Tasks ({tasksCount}){" "}
          <Button
            type="text"
            className="text-brand-500 gap-1 flex-row-centered"
            size="small"
            disabled={!canAddTask}
            onClick={e => {
              e.stopPropagation();
              setleadTaskData({ open: true, mode: "ADD", selectedLead });
            }}
          >
            <PlusOutLinedJSX />
            Add
          </Button>
        </span>
      ),
      content: (
        <LeadTaskList
          selectedLead={selectedLead}
          setleadTaskData={setleadTaskData}
          selectedOppTypeId={selectedOppTypeId}
        />
      ),
    },
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          Appointments ({appointmentsCount}){" "}
          <Button
            type="text"
            className="text-brand-500 gap-1 flex-row-centered"
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleAddAppointment();
            }}
          >
            <PlusOutLinedJSX />
            Add
          </Button>
        </span>
      ),
      content: (
        <AppointmentList
          width="80px"
          leadId={selectedLead?.id}
          selectedLead={selectedLead}
          isFromActiviytCenter
          sorting={{ orderBy: "start_datetime", orderDirection: "desc" }}
          refetch={refetchAppointment}
          isAppointmentChangeFromLead={isAppointmentChangeFromLead}
          onRefetch={() => setRefetchAppointment(true)}
          canView={canViewAppointment}
          setSelectedLead={setSelectedLead}
          leadOpportunities={selectedLead?.leadOpportunities}
          eventId={eventId}
          selectedOppTypeId={selectedOppTypeId}
          onClick={() => {
            // No-op: this AppointmentList is scoped to the currently selected lead.
            // Calling handleLeadDetailProfile here would write the same id to
            // localStorage("clickLeadId"), which the engagements.js useEffect picks
            // up and uses to refetch lead detail and re-pin the lead via onRowClick.
          }}
          handleLeadDetailProfile={handleLeadDetailProfile}
          passUserIds={false}
          refreshLeadListData={refreshLeadListData}
        />
      ),
    },
    {
      header: (
        <span className="flex-row-centered justify-between items-center">
          Opportunities ({opportunitiesCount}){" "}
          <Button
            type="text"
            className="text-brand-500 gap-1 flex-row-centered"
            size="small"
            onClick={e => {
              e.stopPropagation();
              setopportunityDrawer({ open: true, item: selectedLead });
            }}
          >
            <PlusOutLinedJSX />
            Add
          </Button>
        </span>
      ),
      content: (
        <Skeleton loading={spinner} active avatar>
          <OpportunityLayer
            leadId={selectedLead?.id}
            showLinks
            selectedLead={selectedLead}
            statusList={statusList}
            crmIntegrationType={crmIntegration}
            crmIntegrationData={crmIntegrationData}
            refetchLeads={refetchLeads}
            onMessageFilter={camapign => setFilters(camapign)}
            onRefetch={() => setFilters(null)}
            leadOpportunities={selectedLead?.leadOpportunities}
            leadFullname={selectedLead?.fullName}
            crmLeadObj={selectedLead?.crmLeadObj}
            leadUsers={selectedLead?.leadUsers}
            refetchLeadDetails={refetchLeadDetails}
            getMessages={getMessages}
            setActiveOpp={setActiveOpp}
            oppValue={activeOpportunity}
            setSelectedLead={setSelectedLead}
            getLeadOpportunities={getLeadOpportunities}
            selectedOppTypeId={selectedOppTypeId}
            setSelectedOppTypeId={setSelectedOppTypeId}
          />
        </Skeleton>
      ),
    },
    {
      header: (
        <div className="flex-row-centered items-center justify-between">
          <span>Campaign</span>
          {liveCampaign && (
            <Tag className="bg-green-500 text-white rounded-[19px] text-xs font-[600] px-2">
              Live
            </Tag>
          )}
        </div>
      ),
      content: (
        <CampignListView
          leadId={selectedLead?.id}
          showLinks
          onMessageFilter={camapign => setFilters(camapign)}
          onRefetch={() => setFilters(null)}
          handleLeadDetailProfile={handleLeadDetailProfile}
          setliveCampaign={setliveCampaign}
        />
      ),
    },
  ];
  const HorizontalLine = () => <div className="w-full h-[1px] bg-gray-200" />;
  return (
    <div>
      <div className="flex-col-centered gap-3 items-center">
        {showAdd && (
          <>
            <div className="w-full flex flex-row gap-2 items-center">
              {/* <Button
                className="add-lead-btn flex-1 flex-row-centered items-center gap-2 justify-center text-brand-500 border-gray-200 border-[1px]"
                onClick={() => setWishlistDrawerVisible(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="9" x2="15" y2="9" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                  <line x1="12" y1="9" x2="12" y2="15" />
                </svg>{" "}
                Add to wishlist
              </Button> */}

              <Button
                className="add-lead-btn flex-1 flex-row-centered items-center gap-2 justify-center text-brand-500 border-gray-200 border-[1px]"
                onClick={() =>
                  (sessionStorage.getItem("currentLocationId") || "").includes(",")
                    ? message.info(lang.selectLocationFromDropdown)
                    : handleAddPhoneClick()
                }
              >
                <AddLeadIcon /> Add quick lead
              </Button>
            </div>
            <HorizontalLine />
          </>
        )}
        {/* Profile Details start */}
        <div className="flex-col-centered gap-1 mb-6 items-center justify-center relative w-full">
          <Dropdown
            menu={{
              items: [
                {
                  key: "shift-lead",
                  label: "Shift lead",
                  onClick: () => setShiftLeadDrawerVisible(true),
                  disabled: !canChangeLocation,
                },
                {
                  key: "view-audit-log",
                  label: "View audit log",
                  onClick: () => {
                    // TODO: Add view audit log logic
                    message.info("View audit log clicked");
                  },
                  disabled: true,
                },
              ],
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span className="group absolute top-0 right-0 mr-2 cursor-pointer hover:text-grey-800">
              <LuEllipsisVertical
                size={20}
                strokeWidth={1.75}
                className="icon-action-color group-hover:text-inherit"
              />
            </span>
          </Dropdown>
          <Avatar className="bg-error-400">{convertToInitials(selectedLead?.fullName)}</Avatar>
          <span
            className="text-lg flex items-center justify-center font-bold w-full"
            onMouseEnter={() => setshowNameEdit(true)}
            onMouseLeave={() => setshowNameEdit(false)}
          >
            <div className="flex items-center justify-center">
              <span className="ellipsis max-w-[200px] text-center">{selectedLead?.fullName}</span>
              {canEdit && (
                <Popover
                  trigger="click"
                  title="Edit Lead name"
                  placement="bottom"
                  content={
                    <NameForm
                      updateLead={updateLead}
                      loader={updateLeadLoader}
                      nameEditFormRef={nameEditFormRef}
                      setSelectedLead={setSelectedLead}
                    />
                  }
                >
                  <span ref={nameEditFormRef}>
                    <EditPencilIcon
                      className={`cursor-pointer ml-1 ${showNameEdit ? "visible" : "invisible"}`}
                      color="#3B82F6"
                    />
                  </span>
                </Popover>
              )}{" "}
            </div>
          </span>
          {/* Phone number view start */}
          <span>
            {selectedLead &&
            Array.isArray(selectedLead?.phoneNumbers) &&
            (selectedLead?.phoneNumbers || []).length > 0
              ? formatPhoneNumberIntl(
                  selectedLead.phoneNumbers.find(item => item?.isPrimary)?.phone ||
                    selectedLead.phoneNumbers.find(item => item.lookupType === "mobile")?.phone ||
                    ""
                )
              : ""}
          </span>
          {/* Phone number view end */}
          <Tooltip title={profileEmail ? "Send mail" : null}>
            <span
              onClick={() => profileEmail && window.open(`mailto:${profileEmail}`)}
              className={`${
                profileEmail ? "cursor-pointer text-brand-500 ellipsis max-w-[250px]" : ""
              }`}
            >
              {profileEmail || "N/A"}
            </span>
          </Tooltip>
        </div>

        {/* Profile Details end */}
        {/* Profile action starts */}
        <div className="grid grid-cols-5 text-center w-full">
          {leadActions.map((item, index) => (
            <span key={index} className="flex-col-centered items-center gap-1">
              {/* Circular icon button. `group` lets the icon's
                  `group-hover:text-inherit` pick up antd's hover color. */}
              <CustomButton
                shape="circle"
                tooltipTitle={item.tooltip || item.label}
                handleClick={() => item?.onClick && item?.onClick()}
                icon={item.icon}
                // Grey resting state → light blue on hover. `!` on the hover
                // classes so they beat antd's own `.ant-btn:hover` background
                // and border rules.
                className="group bg-gray-100 border-[1px] border-solid border-grey-200 transition-colors hover:!bg-brand-50 hover:!border-brand-200"
              />
              <span>{item.label}</span>
            </span>
          ))}
        </div>
        {/* Profile action end */}
        {/* Opp-type root tabs (Sales / Service). Scopes every section
            below — Notes, Tasks, Appointments, Opportunities, plus the
            messages pane via existing ``selectedOppTypeId`` plumbing.
            Lives in LeadProfile (not the OpportunityLayer) so all
            related data shifts in sync with one click. ``w-full``
            wrapper because the outer ``flex-col-centered items-center``
            column otherwise collapses each child to content width. */}
        <div className="w-full">
          <OppTypeRootTabs
            lead={selectedLead}
            user={user}
            value={selectedOppTypeId}
            onChange={setSelectedOppTypeId}
            unreadByRoot={unreadByRoot}
            noActiveTab={isAllOpportunitySelected}
          />
        </div>
      </div>

      <Collapse
        expandIcon={panelProps => (
          <DownIcon
            className={`${
              panelProps.isActive ? "rotate-0" : "rotate-[-90deg]"
            } transition-all duration-300`}
          />
        )}
        defaultActiveKey={["0", "1", "5", "6"]}
        className="transparent-collapse w-full rounded-none"
      >
        {profileSections.map((item, index) => (
          <Collapse.Panel className="lead-profile-sections" key={index} header={item.header}>
            <div className="px-2">{item.content}</div>
          </Collapse.Panel>
        ))}
      </Collapse>
      <NotesDrawer
        key="key__notedraswer1"
        setSelectedLead={setSelectedLead}
        leadNotesData={leadNotesData}
        setleadNotesData={setleadNotesData}
        // Active root tab — the form's opp-type select defaults to this
        // and the opp dropdown is filtered to opps under this root.
        selectedOppTypeId={selectedOppTypeId}
      />
      <TaskDrawer
        setSelectedLead={setSelectedLead}
        leadTaskData={leadTaskData}
        setleadTaskData={setleadTaskData}
        selectedOppTypeId={selectedOppTypeId}
      />
      <OpportunityDrawer
        crmIntegrationType={crmIntegration}
        mode="ADD"
        opportunityDrawer={opportunityDrawer}
        refetch={refetchLeadDetails}
        // getLeads={refetchLeads}
        setopportunityDrawer={setopportunityDrawer}
        titleContent={
          <div className="flex flex-col">
            <span className="text-lg font-[600]">Add Opportunity for {selectedLead?.fullName}</span>
          </div>
        }
        key="opportunityDrawer-ADD"
        // Active root opp_type tab — defaults the opp-type cascader.
        selectedOppTypeId={selectedOppTypeId}
      />
      <LeadEditDrawer drawerData={leadFormDrawer} setDrawerData={setleadFormDrawer} />
      <WishlistDrawer
        visible={wishlistDrawerVisible}
        onClose={() => setWishlistDrawerVisible(false)}
        selectedLead={selectedLead}
        onSave={wishlistData => {
          // You can add additional logic here to save to backend
        }}
      />
      <ShiftLeadDrawer
        open={shiftLeadDrawerVisible}
        onClose={() => setShiftLeadDrawerVisible(false)}
        refetchLeadDetails={refetchLeadDetails}
        refetchLeads={refetchLeads}
        setSelectedLead={setSelectedLead}
      />
      <ConsentAuditLogDrawer
        open={auditLogDrawerVisible}
        onClose={() => setAuditLogDrawerVisible(false)}
        leadId={selectedLead?.id}
        leadName={selectedLead?.fullName}
      />
      {/* <ChatDrawer chatDrawerData={chatDrawerData} setChatDrawerData={setChatDrawerData} /> */}
    </div>
  );
};

export default LeadProfile;
